import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useState } from "react";
import api from "@/lib/api";
import { useLiveData } from "@/lib/useLiveData";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import {
  LOGO_URL, WATER_TRUCK_URL, WATER_IMG, BIN_IMG, TRUCKING_IMG,
  CAR_2012_URL, CAR_2010_URL, CONTACT,
} from "@/lib/constants";
import {
  Droplets, Trash2, Wrench, Truck, Car, ArrowRight, Phone, Star, Shield, FileText,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.5, ease: "easeOut" } }),
};

function Tag({ children }) {
  return <span className="tag-overline">{children}</span>;
}

function StatCard({ k, v }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5">
      <div className="font-heading text-3xl font-black text-slate-900">{v}</div>
      <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mt-1">{k}</div>
    </div>
  );
}

function ServiceTile({ to, icon: Icon, title, desc, img, price, testid, accent }) {
  return (
    <Link to={to} data-testid={testid} className="group block">
      <motion.div
        initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
        className="bg-white border border-slate-200 rounded-md overflow-hidden hover:border-slate-400 transition-all hover:-translate-y-1"
      >
        <div className={`relative aspect-[16/10] overflow-hidden ${img ? "bg-slate-100" : accent || "bg-[#1D4ED8]"}`}>
          {img ? (
            <img src={img} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <Icon className="h-20 w-20 text-white/80" strokeWidth={1.5} />
            </div>
          )}
          <div className="absolute top-3 left-3 bg-white text-[#1D4ED8] text-xs font-black tracking-wider uppercase px-2 py-1 rounded">
            {price}
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-center gap-2 text-[#E32636]">
            <Icon className="h-5 w-5" strokeWidth={2.5} />
            <span className="font-heading font-bold text-lg">{title}</span>
          </div>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">{desc}</p>
          <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-slate-900">
            Book now <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </motion.div>
    </Link>
  );
}

export default function HomePage() {
  const [content, setContent] = useState(null);
  useLiveData(() => { api.get("/content").then(r => setContent(r.data)).catch(() => {}); });
  const c = content || {};
  return (
    <Layout>
      {c.announcement && (
        <div className="bg-[#FFCE00] text-slate-900 text-sm font-bold py-2 text-center px-4">
          {c.announcement}
        </div>
      )}
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-stripe" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-10 items-center relative">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="lg:col-span-7">
            <Tag>{c.hero_kicker || "Antigua & Barbuda · Lightfoot West"}</Tag>
            <h1 className="mt-3 font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05]">
              {c.hero_title || "Reliable transportation & essential services across Antigua."}
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-2xl">
              {c.hero_subtitle || "Water delivery, bin rentals, vehicle diagnostics, trucking and affordable car rentals — all booked online, priced in XCD, dispatched by Cleavie's crew."}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/portal/rentals">
                <Button data-testid="cta-portal-rentals" className="bg-[#E32636] hover:bg-[#C91F2C] text-white font-bold h-12 px-6">
                  Rentals & Leasing <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
              <Link to="/portal/trucking">
                <Button data-testid="cta-portal-trucking" variant="outline" className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-bold h-12 px-6">
                  Trucking & Industrial
                </Button>
              </Link>
              <a href={`tel:${c.phone || CONTACT.phone}`} className="hidden sm:flex items-center gap-2 font-bold text-slate-900 h-12 px-4 hover:underline">
                <Phone className="h-4 w-4" /> {c.phone || CONTACT.phone}
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
              <StatCard k="Portals" v="2" />
              <StatCard k="Services" v="5+" />
              <StatCard k="Coverage" v="A&B" />
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
                      className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] rounded-md overflow-hidden border border-slate-200 shadow-sm">
              <img src={WATER_TRUCK_URL} alt="Cleavie's water truck" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 text-white">
                <div className="tag-overline text-[#FFCE00]">Our fleet at work</div>
                <div className="font-heading font-black text-2xl mt-1">One platform · Two portals.</div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 hidden lg:flex items-center gap-3 bg-white border border-slate-200 rounded-md px-4 py-3 shadow-sm">
              <Shield className="h-5 w-5 text-[#1D4ED8]" />
              <div className="text-xs">
                <div className="font-bold text-slate-900">Locally owned</div>
                <div className="text-slate-500">Trusted island-wide</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PORTALS */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="flex items-end justify-between mb-10">
            <div>
              <Tag>One platform · Two portals</Tag>
              <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-slate-900 mt-2">Pick your experience.</h2>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Rentals & Leasing */}
            <Link to="/portal/rentals" className="group block" data-testid="portal-card-rentals-home">
              <div className="bg-[#E32636] text-white rounded-md overflow-hidden border border-[#C91F2C] hover:border-white transition-all hover:-translate-y-1">
                <div className="relative aspect-[16/9] overflow-hidden bg-white flex items-center justify-center p-6">
                  <img src={CAR_2012_URL} alt="Honda Fit" className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-7">
                  <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#FFCE00]">Portal 1</div>
                  <div className="font-heading font-black text-2xl mt-1">Vehicle Rentals & Leasing</div>
                  <p className="text-sm text-white/90 mt-2">Daily rentals · Lease applications · Documents · Payments</p>
                  <div className="mt-5 inline-flex items-center gap-1 font-bold">Enter portal <ArrowRight className="h-4 w-4" /></div>
                </div>
              </div>
            </Link>
            {/* Trucking */}
            <Link to="/portal/trucking" className="group block" data-testid="portal-card-trucking-home">
              <div className="bg-[#0F172A] text-white rounded-md overflow-hidden border border-slate-900 hover:border-[#FFCE00] transition-all hover:-translate-y-1">
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img src={WATER_TRUCK_URL} alt="Water truck" className="w-full h-full object-cover group-hover:scale-105 transition-all duration-700" />
                </div>
                <div className="p-7">
                  <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#FFCE00]">Portal 2</div>
                  <div className="font-heading font-black text-2xl mt-1">Trucking & Industrial Services</div>
                  <p className="text-sm text-slate-300 mt-2">Water · Bins · Diagnostics · Trucking</p>
                  <div className="mt-5 inline-flex items-center gap-1 font-bold">Enter portal <ArrowRight className="h-4 w-4" /></div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES GRID removed — landing only offers the two portals */}

      {/* LOCAL PRIDE / CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div className="relative aspect-[4/3] rounded-md overflow-hidden border border-slate-200 bg-gradient-to-br from-red-50 via-white to-blue-50 flex items-center justify-center p-10">
            <img src={LOGO_URL} alt="Cleavie's Enterprise" className="max-h-full max-w-full object-contain" />
          </div>
          <div>
            <Tag>From the islands · For the islands</Tag>
            <h2 className="font-heading font-black text-3xl sm:text-4xl mt-2 text-slate-900">Born in Lightfoot West.</h2>
            <p className="mt-4 text-slate-600">
              Cleavie's Enterprise is a local family business serving Antigua &amp; Barbuda with dependable services and honest pricing.
              From your morning water delivery to a weekend rental — we keep the island moving.
            </p>
            <div className="mt-6 flex flex-col gap-2 text-sm">
              <div className="flex items-center gap-2"><Star className="h-4 w-4 text-[#FFCE00]" />Locally owned &amp; operated</div>
              <div className="flex items-center gap-2"><Star className="h-4 w-4 text-[#FFCE00]" />Transparent XCD pricing</div>
              <div className="flex items-center gap-2"><Star className="h-4 w-4 text-[#FFCE00]" />Fast online booking with email confirmations</div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
